import { UrlHelpers } from '@/shared/services/UrlHelpers'
import { FileSystemRecord } from '@/shared/sync/mediaRepository/FileSystemRecord'
import { openDB, IDBPDatabase, IDBPTransaction } from 'idb'

export const DB_NAME = 'MyDatabase'
export const STORE_NAME = 'MyStore'
// Compound index used by SafariIndexedDB for metadata-only listing (see listMetadata).
// Booleans are NOT valid IndexedDB keys, so folder-ness is encoded as numeric `_kind`.
export const INDEX_NAME = 'byMeta'
export const INDEX_KEYPATH = ['_kind', '_path', 'modificationDate', 'size', 'systemFileNumber']

/** Upgrade-transaction type handed to `createSchema` so subclasses can create indexes. */
export type UpgradeTx = IDBPTransaction<unknown, string[], 'versionchange'>

/**
 * Lightweight, files-only metadata shape returned by `listMetadata`. Carries only the
 * scalar fields sync needs to build a file index, so the (potentially large) blob bytes
 * are never read just to enumerate a project.
 */
export interface FileMetadataEntry {
  path: string
  systemFileNumber: number
  modificationDate: Date
  size: number
  isFolder: boolean
}

/**
 * Shared IndexedDB machinery (connection lifecycle, self-heal, CRUD, prefix scans). The
 * per-browser differences (storage format + metadata listing strategy + schema) are
 * deferred to the abstract hooks and implemented by the subclasses (DefaultIndexedDB,
 * SafariIndexedDB).
 */
export abstract class BaseIndexedDB {
  // ---- connection state (per instance) ----
  protected dbPromise: Promise<IDBPDatabase> | null = null
  protected opMutex: Promise<void> = Promise.resolve()
  // Lazily seeded from `schemaVersion` on first open (abstract members can't be read in
  // the base constructor); may be bumped by self-heal/version recovery afterwards.
  protected currentVersion: number = 0

  // ---------- Abstract contract (per-browser behavior) ----------

  /** On-disk schema version for this strategy (bumped when the schema/indexes change). */
  protected abstract get schemaVersion(): number

  /** Creates the object store (and any subclass-specific indexes) during an upgrade. */
  protected abstract createSchema(db: IDBPDatabase, tx?: UpgradeTx): void

  /** Transforms a record into its on-disk form (e.g. Blob -> ArrayBuffer for Safari). */
  protected abstract toStorable<T extends object>(value: T, key: string): Promise<T>

  /** Reconstructs the in-memory record after a read (e.g. ArrayBuffer -> Blob). */
  protected abstract hydrateForRead<T>(value: T): Promise<T>

  /** Enumerates files under a prefix WITHOUT reading blob bytes. */
  abstract listMetadata(prefix: string): Promise<FileMetadataEntry[]>

  // ---- open with robust lifecycle handling ----
  /** Robust open: handles versionchange/closing, HMR/multi-tab scenarios */
  getDB(): Promise<IDBPDatabase> {
    if (!this.dbPromise) {
      if (!this.currentVersion) this.currentVersion = this.schemaVersion
      this.dbPromise = this._openAtLeast(this.currentVersion)
    }
    return this.dbPromise
  }

  /**
   * Opens the DB at `version`, recovering from a VersionError. `currentVersion` resets to
   * the base schema version on every page load, but a previous session's self-heal repair
   * may have bumped the on-disk DB to a higher version. Opening at the lower base version
   * then throws "An attempt was made to open a database using a lower version than the
   * existing version" and blocks every read/write. We recover by probing the actual
   * on-disk version and reopening at >= it.
   */
  private async _openAtLeast(version: number): Promise<IDBPDatabase> {
    try {
      return await this._open(version)
    } catch (e) {
      this.dbPromise = null
      if (!this._isVersionErr(e)) throw e

      const existing = await this._probeVersion()
      this.currentVersion = Math.max(existing, version)
      return this._open(this.currentVersion)
    }
  }

  /** Single canonical open with the store-creation upgrade + lifecycle handlers. */
  private _open(version: number): Promise<IDBPDatabase> {
    const self = this
    let dbRef: IDBPDatabase | null = null

    return openDB(DB_NAME, version, {
      upgrade(db, _oldVersion, _newVersion, tx) {
        self.createSchema(db, tx as unknown as UpgradeTx)
      },

      blocking() {
        try { dbRef?.close() } finally { self.dbPromise = null }
      },
      blocked() {
        try { dbRef?.close() } finally { self.dbPromise = null }
      },
      terminated() {
        self.dbPromise = null
      },
    }).then(db => {
      dbRef = db
      // Keep the version in sync with what is actually on disk so we never request a
      // lower version than exists.
      self.currentVersion = db.version
      db.addEventListener('versionchange', () => {
        try { db.close() } finally { self.dbPromise = null }
      })
      return db
    })
  }

  /** Opens without forcing a version to read the current on-disk version, then closes. */
  private async _probeVersion(): Promise<number> {
    const db = await openDB(DB_NAME)
    const version = db.version
    db.close()
    return version
  }

  // ---- tx helpers (so sync throws from transaction() occur inside retried op) ----
  protected _txRO(db: IDBPDatabase) { return db.transaction(STORE_NAME, 'readonly') }
  protected _txRW(db: IDBPDatabase) { return db.transaction(STORE_NAME, 'readwrite') }

  // ---- error classification ----
  private _isClosingErr(e: any): boolean {
    const name = e?.name ?? e?.target?.error?.name ?? e?.error?.name ?? ''
    const msg = e?.message ?? e?.target?.error?.message ?? e?.error?.message ?? ''
    return (
      name === 'InvalidStateError' ||
      name === 'TransactionInactiveError' ||
      name === 'AbortError' ||
      /closing|finished|inactive|not active|aborted|connection.*closing/i.test(msg)
    )
  }

  private _isSchemaErr(e: any): boolean {
    const name = e?.name ?? e?.target?.error?.name ?? e?.error?.name ?? ''
    const msg = e?.message ?? e?.target?.error?.message ?? e?.error?.message ?? ''
    // Chrome/Edge: NotFoundError; Firefox: NotFoundError; Safari: sometimes "InvalidAccessError"
    return (
      name === 'NotFoundError' ||
      /object store.*not found|does not exist|InvalidAccessError/i.test(msg)
    )
  }

  // Raised when opening the DB at a version lower than the one already on disk.
  private _isVersionErr(e: any): boolean {
    const name = e?.name ?? e?.target?.error?.name ?? e?.error?.name ?? ''
    const msg = e?.message ?? e?.target?.error?.message ?? e?.error?.message ?? ''
    return (
      name === 'VersionError' ||
      /lower version than the existing version|requested version.*less than|version.*less than.*existing/i.test(msg)
    )
  }

  private _sleep(ms: number) { return new Promise(res => setTimeout(res, ms)) }

  // ---- schema self-heal: bump version by 1 and recreate missing store/index ----
  protected async _repairSchema(staleDb: IDBPDatabase | null): Promise<IDBPDatabase> {
    const self = this
    try { staleDb?.close?.() } catch {}
    this.dbPromise = null

    // Bump to a strictly higher version than any existing handle
    const nextVersion = Math.max(
      (staleDb?.version ?? this.currentVersion) + 1,
      this.currentVersion + 1
    )
    this.currentVersion = nextVersion

    // Open with an upgrade that ensures the schema exists
    return openDB(DB_NAME, this.currentVersion, {
      upgrade(db, _oldVersion, _newVersion, tx) {
        self.createSchema(db, tx as unknown as UpgradeTx)
      },
      blocked() { /* noop; caller serialized ops */ },
      blocking() { /* noop; short upgrade */ },
      terminated() { self.dbPromise = null },
    }).then(db => {
      // Attach versionchange close handler as in normal open
      db.addEventListener('versionchange', () => {
        try { db.close() } finally { self.dbPromise = null }
      })
      self.dbPromise = Promise.resolve(db)
      return db
    })
  }

  /**
   * Serialize ops; retry on "closing" races; **self-heal** on schema NotFound.
   * Up to 3 attempts total, with tiny exponential backoff between retries.
   */
  protected async _withDBRetry<T>(op: (db: IDBPDatabase) => Promise<T>): Promise<T> {
    // Serialize individual attempts (not backoff) to avoid long queues when retrying
    const runExclusive = async <R>(fn: () => Promise<R>) => {
      const prev = this.opMutex
      let release!: () => void
      this.opMutex = new Promise<void>(res => (release = res))
      await prev
      try { return await fn() } finally { release() }
    }

    let attempt = 0
    let lastErr: any

    while (attempt < 3) {
      let db: IDBPDatabase | null = null
      try {
        return await runExclusive(async () => {
          db = await this.getDB()
          try {
            return await op(db) // covers sync throws at transaction() and async rejections
          } catch (e) {
            lastErr = e

            // Handle "store missing" first: repair schema, then retry (within the lock)
            if (this._isSchemaErr(e)) {
              const repaired = await this._repairSchema(db)
              return op(repaired)
            }

            throw e
          }
        })
      } catch (e) {
        lastErr = e

        // Handle "connection is closing" races
        if (this._isClosingErr(e)) {
          try { db?.close?.() } catch {}
          this.dbPromise = null
          attempt++
          await this._sleep([0, 20, 60][Math.min(attempt, 2)] ?? 60)
          continue
        }

        // If schema repair inside the lock still failed, try again before giving up
        if (this._isSchemaErr(e)) {
          attempt++
          continue
        }

        // Anything else: propagate
        throw e
      }
    }

    throw lastErr
  }

  // ---------- Public API ----------

  async list(path: string): Promise<[string, FileSystemRecord][]> {
    return this._withDBRetry(async db => {
      const tx = this._txRO(db)
      const store = tx.store

      const lower = path
      const upper = path + '\uffff'
      const range = IDBKeyRange.bound(lower, upper)

      const out: [string, FileSystemRecord][] = []
      let cursor = await store.openCursor(range)
      while (cursor) {
        const key = String(cursor.primaryKey)
        const raw = cursor.value as FileSystemRecord
        Object.setPrototypeOf(raw, FileSystemRecord.prototype)
        // Hydrate the blob using the same logic as getItem/getAll for consistency
        await this.hydrateForRead(raw)
        out.push([key, raw])
        cursor = await cursor.continue()
      }
      await tx.done
      return out
    })
  }

  async setItem<T extends object>(key: string, value: T): Promise<void> {
    return this._withDBRetry(async db => {
      // Convert the record to its on-disk form (per-browser; see subclass toStorable).
      const storable = await this.toStorable(value, key)
      const tx = this._txRW(db)
      await tx.store.put(storable as any, key)
      await tx.done
    })
  }

  async getItem<T>(key: string): Promise<T | undefined> {
    return this._withDBRetry(async db => {
      const tx = this._txRO(db)
      const val = (await tx.store.get(key)) as any
      await tx.done
      return (await this.hydrateForRead(val)) as T | undefined
    })
  }

  async deleteItems(keys: string[], includeMemory: boolean = true): Promise<void> {
      if (!keys || keys.length === 0) return;
      // Dedupe to avoid redundant reads/deletes in a single transaction
      const uniqueKeys = [...new Set(keys)];

      return this._withDBRetry(async (db) => {
        const tx = this._txRW(db);

        if (includeMemory) {
          // 1) Read all records in parallel so we can revoke blob URLs before deletion
          const records = await Promise.all(uniqueKeys.map((key) => tx.store.get(key)));

          // 2) Revoke any object URLs (don't let a revocation error abort the transaction)
          for (const rec of records) {
            const blobUrl: string | undefined = rec?._objectBlobUrl;
            if (blobUrl) {
              try {
                UrlHelpers.revokeObjectURLSmart(blobUrl);
              } catch {
                // Ignore revocation errors; deletion should still proceed
              }
            }
          }
        }
        // 3) Delete all keys in parallel and wait for requests to be queued/finished
        await Promise.all(uniqueKeys.map((key) => tx.store.delete(key)));

        // 4) Ensure the transaction fully commits
        await tx.done;
      });
  }

  async deleteItem(key: string): Promise<void> {
    return this._withDBRetry(async db => {
      const tx = this._txRW(db)
      await tx.store.delete(key)
      await tx.done
    })
  }

  async getAll<T>(): Promise<T[]> {
    return this._withDBRetry(async db => {
      const tx = this._txRO(db)
      const vals = (await tx.store.getAll()) as T[]
      await tx.done
      return Promise.all(vals.map(v => this.hydrateForRead(v)))
    })
  }

  async getAllKeys(): Promise<string[]> {
    return this._withDBRetry(async db => {
      const tx = this._txRO(db)
      const keys = (await tx.store.getAllKeys()) as string[]
      await tx.done
      return keys
    })
  }

  /**
   * Copy/link via IDB with auto-retry. Two-phase to avoid long readwrite tx
   * waiting on arrayBuffer().
   */
  async copyIsHardCopyDirect(
    sourcePath: string,
    destinationPath: string,
    isHardCopy: boolean
  ): Promise<boolean> {
    return this._withDBRetry(db => this._doCopy(db, sourcePath, destinationPath, isHardCopy))
  }

  // ---------- Internals ----------

  private _doCopy(
    db: IDBPDatabase,
    sourcePath: string,
    destinationPath: string,
    isHardCopy: boolean
  ): Promise<boolean> {
    return db.get(STORE_NAME, sourcePath).then((root: FileSystemRecord | undefined) => {
      if (!root) return false

      // ---------- FILE ----------
      if (!root.isFolder) {
        // `root` is the raw stored record: its `blob` is an ArrayBuffer (Safari format) or
        // a native Blob (default). toStorable normalizes it to the active storage form;
        // IndexedDB copies the bytes on put, so a hard copy gets its own bytes.
        const newFileNo = isHardCopy ? this._gen12() : root.systemFileNumber
        const now = new Date()
        const toStore: FileSystemRecord = Object.assign({}, root, {
          systemFileNumber: newFileNo,
          isFolder: false,
          undoDate: root.undoDate ?? root.modificationDate ?? now,
        })

        const parts = destinationPath.split('.')
        if (parts.length > 1) (toStore as any).format = parts.pop()!.split('?')[0]

        return this.toStorable(toStore, destinationPath).then(storable => {
          const tx = this._txRW(db)
          return tx.store.put(storable, destinationPath).then(() => tx.done.then(() => true))
        })
      }

      // ---------- FOLDER ----------
      // Phase 1: READ (readonly) — gather all keys+values under prefix & deep-copy blobs if needed
      const lower = sourcePath
      const upper = sourcePath + '\uffff'
      const range = IDBKeyRange.bound(lower, upper)

      const rtx = this._txRO(db)
      const rstore = rtx.store

      return Promise.all<[IDBValidKey[], FileSystemRecord[]]>([
        rstore.getAllKeys(range) as any,
        rstore.getAll(range) as any,
      ])
        .then(([keys, vals]) => {
          const now = new Date()
          const tasks: Array<{ key: string; val: FileSystemRecord }> = []

          // ensure destination root folder record will exist
          tasks.push({
            key: destinationPath,
            val: new FileSystemRecord(null, now, now, now, 0, this._gen12(), true, {}),
          })

          const prepPromises = keys.map((k, i) => {
            const srcKey = String(k)
            const rel = srcKey.substring(sourcePath.length)
            const dstKey = destinationPath + rel
            if (dstKey === destinationPath) return Promise.resolve()

            const rec = vals[i] as FileSystemRecord

            if (rec.isFolder) {
              tasks.push({ key: dstKey, val: Object.assign({}, rec, { isFolder: true }) })
              return Promise.resolve()
            }

            // See file-branch comment: toStorable normalizes ArrayBuffer/native-Blob
            // records to the active storage form.
            const newFileNo = isHardCopy ? this._gen12() : rec.systemFileNumber
            const fileRec: FileSystemRecord = Object.assign({}, rec, {
              systemFileNumber: newFileNo,
              isFolder: false,
              undoDate: rec.undoDate ?? rec.modificationDate ?? now,
            })

            const parts = dstKey.split('.')
            if (parts.length > 1) (fileRec as any).format = parts.pop()!.split('?')[0]

            return this.toStorable(fileRec, dstKey).then(storable => {
              tasks.push({ key: dstKey, val: storable })
            })
          })

          return Promise.all(prepPromises).then(() => rtx.done.then(() => tasks))
        })
        .then(tasks => {
          // Phase 2: WRITE (single readwrite tx) — batch puts
          const wtx = this._txRW(db)
          const wstore = wtx.store

          let chain = Promise.resolve<void>(undefined)
          for (const t of tasks) {
            chain = chain.then(() => wstore.put(t.val, t.key)).then(() => undefined)
          }
          return chain.then(() => wtx.done).then(() => true)
        })
    })
  }

  protected _gen12(): number {
    return Number(Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join(''))
  }

  async deleteByPrefix(prefix: string, idsToPreserve: string[] = []): Promise<void> {
    return this._withDBRetry(async db => {
      const tx = this._txRW(db);
      const store = tx.store;

      const lower = prefix;
      const upper = prefix + '\uffff';
      const range = IDBKeyRange.bound(lower, upper);

      let cursor = await store.openCursor(range);
      while (cursor) {
        let strPrimaryKey = cursor.primaryKey ? String(cursor.primaryKey) : null;
        if (strPrimaryKey && idsToPreserve.find(id => strPrimaryKey.includes(id))) {
          cursor = await cursor.continue();
          continue;
        }
        const record = cursor.value as FileSystemRecord;
        let bloblUrl = record?._objectBlobUrl;
        if (bloblUrl) {
          UrlHelpers.revokeObjectURLSmart(bloblUrl);
        }
        await cursor.delete();
        cursor = await cursor.continue();
      }

      await tx.done;
    });
  }
}
